import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

type Theme = 'light' | 'dark';
export type { PageStyle } from './theme-default';
import {
  resolvePageStyle,
  pageStyleKey as styleKey,
  defaultStyleKey,
  type PageStyle,
} from './theme-default';
function readStyle(): PageStyle {
  try {
    return resolvePageStyle(localStorage);
  } catch {
    return 'island';
  }
}
export type ThemePreference = Theme | 'system';
const storageKey = 'work-break-theme';
const ThemeContext = createContext<{
  theme: Theme;
  pageStyle: PageStyle;
  setPageStyle: (value: PageStyle) => void;
  preference: ThemePreference;
  setTheme: (value: ThemePreference) => void;
} | null>(null);

function readPreference(): Theme | null {
  try {
    const saved = localStorage.getItem(storageKey);
    return saved === 'light' || saved === 'dark' ? saved : null;
  } catch {
    return null;
  }
}

function systemTheme(): Theme {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  document.documentElement.dataset.theme = theme;
  document.documentElement.style.colorScheme = theme;
  document
    .querySelector('meta[name="theme-color"]')
    ?.setAttribute('content', theme === 'dark' ? '#0e1522' : '#f3f5f9');
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [pageStyle, updatePageStyle] = useState<PageStyle>(readStyle);
  function setPageStyle(value: PageStyle) {
    updatePageStyle(value);
    try {
      localStorage.setItem(styleKey, value);
    } catch {}
  }
  const [preference, setPreference] = useState<Theme | null>(readPreference);
  const [system, setSystem] = useState<Theme>(systemTheme);
  const theme = preference ?? system;

  useEffect(() => {
    document.documentElement.dataset.pageStyle = pageStyle;
  }, [pageStyle]);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const onSystemChange = () => setSystem(media.matches ? 'dark' : 'light');
    const onStorage = (event: StorageEvent) => {
      if (event.key === styleKey || event.key === defaultStyleKey || event.key === null)
        updatePageStyle(readStyle());
      if (event.key === storageKey || event.key === null) setPreference(readPreference());
    };
    onSystemChange();
    media.addEventListener('change', onSystemChange);
    window.addEventListener('storage', onStorage);
    return () => {
      media.removeEventListener('change', onSystemChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  function setTheme(value: ThemePreference) {
    const next = value === 'system' ? null : value;
    applyTheme(next ?? systemTheme());
    setPreference(next);
    try {
      if (next === null) localStorage.removeItem(storageKey);
      else localStorage.setItem(storageKey, next);
    } catch {
      // Keep the user's choice for this session if storage is blocked.
    }
  }

  return (
    <ThemeContext.Provider
      value={{ pageStyle, setPageStyle, theme, preference: preference ?? 'system', setTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('ThemeProvider is required');
  return context;
}
